import { css } from '@emotion/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { COLORS2 } from '@/Client/Styles';
import { ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';

import { LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { InlineTaskEditor } from './InlineTaskEditor';

const TASK_CATEGORY_SECTION_CONTAINER_STYLE = css`
  label: TASK_CATEGORY_SECTION_CONTAINER_STYLE;
  width: 100%;
`;

const TASK_CATEGORY_HEADER_STYLE = css`
  label: TASK_CATEGORY_HEADER_STYLE;
  ${TYPOGRAPHY2.xSmallBold};
  display: flex;
  align-items: center;
  padding-left: ${LAYOUT2.spacing2}px;
  position: sticky;
  min-height: ${LAYOUT2.spacing3}px;
  top: 0;
  text-transform: uppercase;
  z-index: 10;
  background-color: ${COLORS2.alwaysDark.background.light};
  color: ${COLORS2.alwaysDark.text.lightGray};
  gap: ${LAYOUT2.spacing1}px;
`;

const TASK_CATEGORY_HEADER_TITLE_STYLE = css`
  label: TASK_CATEGORY_HEADER_TITLE_STYLE;
  ${TYPOGRAPHY2.xSmallBold};
  flex-grow: 1;
`;

export const ClusteredTasksEditor = React.memo(({
  baselineTasksById,
  groupId,
  initialExpandedTaskId,
  specialMode,
  stickyTitleTop,
  clusterOrder,
  taskClusters,
}: {
  baselineTasksById?: Record<string, ScanTaskOptimized>;
  groupId: string;
  initialExpandedTaskId?: string;
  specialMode?: 'review' | 'changesRequested';
  stickyTitleTop?: number;
  clusterOrder: string[];
  taskClusters: Record<string, ScanTaskOptimized[]>;
}) => {
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('taskId');
  const [expandedTaskId, setExpandedTaskId] = useState<string | undefined>(initialExpandedTaskId);
  const tasks = useMemo(() => Object.values(taskClusters).flat(), [taskClusters]);

  useEffect(() => {
    if (initialExpandedTaskId) {
      setExpandedTaskId(initialExpandedTaskId);
      setTimeout(() => {
        const taskElement = document.getElementById(`task-${initialExpandedTaskId}`);
        if (taskElement) {
          taskElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [initialExpandedTaskId]);

  useEffect(() => {
    if (taskId && tasks.some(task => task.id === taskId)) {
      setExpandedTaskId(taskId);
      setTimeout(() => {
        const taskElement = document.getElementById(`task-${taskId}`);
        if (taskElement) {
          taskElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [taskId, tasks]);

  return (
    <>
      {clusterOrder.map(taskCategory => {
        const filteredTasks = taskClusters[taskCategory];
        const result = tasks.length > 0 && (
          <div key={taskCategory} css={TASK_CATEGORY_SECTION_CONTAINER_STYLE}>
            <div css={TASK_CATEGORY_HEADER_STYLE} style={{ top: stickyTitleTop || 0 }}>
              <div css={TASK_CATEGORY_HEADER_TITLE_STYLE}>{taskCategory}</div>
            </div>
            {filteredTasks.flatMap((task, taskIndex) => (
              <InlineTaskEditor
                baselineTask={baselineTasksById?.[task.id]}
                groupId={groupId}
                specialMode={specialMode}
                key={`${task.id}`}
                task={task}
                expanded={expandedTaskId === task.id}
                setExpandedTaskId={setExpandedTaskId}
              />
            ))}
          </div>
        );
        return result;
      })}
    </>
  );
});
